import type { CastControls } from '../lib/useCast.js';
import { castButtonModel } from './castButtonModel.js';
import styles from './CastButton.module.css';

export const IDLE_PATH =
  'M1 18v3h3c0-1.66-1.34-3-3-3zm0-4v2c2.76 0 5 2.24 5 5h2c0-3.87-3.13-7-7-7zm0-4v2c4.97 0 9 4.03 9 9h2c0-6.08-4.93-11-11-11zM21 3H3c-1.1 0-2 .9-2 2v3h2V5h18v14h-7v2h7c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z';

/** Same glyph with the screen filled in — the standard "casting now" state. */
const LIVE_PATH = `${IDLE_PATH} M19 7H5v1.63c3.96 1.28 7.09 4.41 8.37 8.37H19V7z`;

/**
 * Thin wiring: every label, note and disabled decision lives in
 * `castButtonModel` — including the tab-cast workaround each dead path names.
 *
 * `compact` is for a list: a console with six tables would otherwise print the
 * same paragraph six times. The note still reaches the owner as the button's
 * title and accessible name, and the list prints the how-to once beneath it.
 */
export function CastButton({
  cast,
  /** Set when the TV URL is unreachable from a cast device (localhost). */
  blockedReason,
  /** Drop the note paragraph — a row in a list has no space for it. */
  compact = false,
}: {
  cast: CastControls;
  blockedReason?: string | null;
  compact?: boolean;
}) {
  const vm = castButtonModel({
    status: cast.status,
    available: cast.available,
    checked: cast.checked,
    block: cast.block,
    error: cast.error,
    blockedReason,
  });

  return (
    // A span, not a div: a table row nests this inside phrasing content.
    <span className={styles.wrap}>
      <button
        type="button"
        className={[styles.button, vm.live ? styles.live : '', vm.connecting ? styles.connecting : '']
          .filter(Boolean)
          .join(' ')}
        onClick={() => (vm.live ? cast.stop() : cast.start())}
        disabled={vm.disabled}
        title={vm.title}
        aria-label={vm.title}
      >
        <svg className={styles.icon} viewBox="0 0 24 24" aria-hidden="true">
          <path d={vm.live ? LIVE_PATH : IDLE_PATH} />
        </svg>
        <span>{vm.label}</span>
      </button>
      {vm.note && (!compact || vm.disabled) && <span className={styles.note}>{vm.note}</span>}
    </span>
  );
}
